import type { CSSProperties } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

import type { Dimensions, Position } from '../../../shared/interfaces';
import { getSignatureColor, SignatureBaseButton } from '../../../shared/components/SignatureBaseButton';
import type { AssignedSignee } from 'services/entities/SignatureTypes';

import './Signature.less';
import { useExpiredSignature } from '../../../../../hooks/useExpiredSignature.ts';

export interface PreviewSignatureProps {
  signatureId: string;
  assignedSignee?: AssignedSignee | null;
  handleClick?: () => void;
  position: Position;
  dimensions: Dimensions;
  contentId: string;
}

const PreviewUnsignedSignature: React.FunctionComponent<PreviewSignatureProps> = ({
  signatureId,
  assignedSignee = null,
  position,
  dimensions,
  handleClick,
  contentId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isDisabled, tooltipMessage } = useExpiredSignature({ contentId });
  const signee = assignedSignee;

  const customStylePositioning: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: `translate(${position.x}px, ${position.y}px)`,
  };

  const defaultStyle: CSSProperties = {
    width: dimensions.width,
    height: dimensions.height,
  };

  const disabledStyles: CSSProperties = {
    backgroundColor: theme.palette.action.disabledBackground,
    borderColor: theme.palette.action.disabled,
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  };

  const button = (styles: CSSProperties) => (
    <SignatureBaseButton style={styles} color={getSignatureColor(signee?.email)} data-signature-id={signatureId}>
      {t('signature')}
    </SignatureBaseButton>
  );

  return (
    <div className="unsigned_signature" style={customStylePositioning} onClick={isDisabled ? undefined : handleClick}>
      <div data-testid="unsigned_signature_draggable" />
      {isDisabled ? (
        <Tooltip title={tooltipMessage} placement="top">
          {button({ ...defaultStyle, ...disabledStyles })}
        </Tooltip>
      ) : (
        button({ ...defaultStyle })
      )}
    </div>
  );
};

export default PreviewUnsignedSignature;
